import { memo, useCallback, useMemo } from 'react';

import { DateRange } from 'react-day-picker';

import DatePicker from 'components/DatePicker/DatePicker';
import {
  DatePickerMode,
  DatePickerValue
} from 'components/DatePicker/DatePicker.types';

import { DatePickerFilterProps } from './types';

const formatDateOnly = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const DatePickerFilter = memo(
  ({
    label,
    onApply,
    initialValue,
    isLocalTime = true
  }: DatePickerFilterProps) => {
    const initialDateRange = useMemo(() => {
      if (!initialValue) {
        return undefined;
      }

      try {
        const parsed = JSON.parse(initialValue);

        const parseDateOnly = (dateStr: string) => {
          if (dateStr.includes('T')) {
            const date = new Date(dateStr);
            if (isLocalTime) {
              return date;
            }
            return new Date(
              date.getUTCFullYear(),
              date.getUTCMonth(),
              date.getUTCDate()
            );
          }

          const [year, month, day] = dateStr.split('-').map(Number);
          return new Date(year, month - 1, day);
        };

        return {
          from: parsed.startDate ? parseDateOnly(parsed.startDate) : undefined,
          to: parsed.endDate ? parseDateOnly(parsed.endDate) : undefined
        };
      } catch {
        return undefined;
      }
    }, [initialValue, isLocalTime]);

    const handleApply = useCallback(
      (value: DatePickerValue) => {
        if (!value) {
          onApply('');
          return;
        }

        const dateRange = value as DateRange;

        if (!dateRange.from) {
          onApply('');
          return;
        }

        const startDate = dateRange.from;
        const endDate = dateRange.to || dateRange.from;

        const result = JSON.stringify({
          startDate: formatDateOnly(startDate),
          endDate: formatDateOnly(endDate)
        });
        onApply(result);
      },
      [onApply]
    );

    const handleClear = useCallback(() => {
      onApply('');
    }, [onApply]);

    return (
      <div className="flex flex-col rounded-md">
        <DatePicker
          mode={DatePickerMode.RANGE}
          numberOfMonths={1}
          shouldDisplayTime={false}
          value={initialDateRange}
          onApply={handleApply}
          onClear={handleClear}
          shouldExcludeFuture={true}
          label={label}
          placeholder="Select date range"
          displayInUTC={!isLocalTime}
          alwaysOpen={true}
          hideTrigger={true}
          popoverClassName="p-2"
        />
      </div>
    );
  }
);

DatePickerFilter.displayName = 'DatePickerFilter';
